/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function cleanMessage(value) {
  return String(value || "")
    .replace(/^firebaseerror:\s*/i, "")
    .replace(/^internal:\s*/i, "")
    .replace(/^failed-precondition:\s*/i, "")
    .replace(/^permission-denied:\s*/i, "")
    .trim();
}

function friendlyError(err) {
  const code = String(err?.code || "").toLowerCase();
  const message = cleanMessage(err?.message || "");

  if (code.includes("auth/email-already-in-use")) {
    return "This email already has an account. Sign in instead to accept the invite.";
  }
  if (code.includes("auth/invalid-email")) {
    return "This invite email is invalid.";
  }
  if (code.includes("auth/weak-password")) {
    return "Password must be at least 6 characters.";
  }
  if (code.includes("auth/wrong-password")) {
    return "That password is not correct.";
  }
  if (code.includes("auth/user-not-found")) {
    return "No existing account was found for this email.";
  }
  if (code.includes("auth/invalid-credential")) {
    return "That email or password is not correct.";
  }
  if (code.includes("functions/not-found")) {
    return "Invite not found.";
  }
  if (code.includes("functions/permission-denied")) {
    return "This invite cannot be completed with the current account.";
  }
  if (code.includes("functions/failed-precondition")) {
    if (/expired/i.test(message)) return "This invite has expired.";
    if (/revoked/i.test(message)) return "This invite has been revoked.";
    if (/rejected/i.test(message)) return "This invite is no longer valid.";
    if (/deleted/i.test(message)) return "This invite is no longer available.";
    if (/missing token/i.test(message)) return "This invite is missing its token.";
    if (/already used/i.test(message) || /already accepted/i.test(message)) {
      return "This invite has already been used.";
    }
    return message || "This invite is no longer available.";
  }
  if (code.includes("functions/internal")) {
    return message || "We could not finish accepting this invite right now.";
  }
  if (message) return message;
  return "Something went wrong.";
}

function roleLabelFromInvite(invite) {
  return String(invite?.requestedRole || invite?.role || "teacher").replace(
    /^./,
    (m) => m.toUpperCase()
  );
}

export default function AcceptStaffInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = String(searchParams.get("token") || "").trim();
  const schoolIdFromUrl = String(searchParams.get("schoolId") || "").trim();
  const inviteIdFromUrl = String(searchParams.get("inviteId") || "").trim();

  const [booting, setBooting] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [inviteData, setInviteData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [inviteId, setInviteId] = useState("");

  const [accountMode, setAccountMode] = useState("new");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const previewStaffInvite = useMemo(
    () => httpsCallable(functions, "previewStaffInvite"),
    []
  );

  const acceptInviteCallable = useMemo(
    () => httpsCallable(functions, "acceptStaffInviteV2"),
    []
  );

  useEffect(() => {
    let active = true;

    async function loadInvite() {
      try {
        setBooting(true);
        setError("");

        if (!token || !schoolIdFromUrl || !inviteIdFromUrl) {
          if (!active) return;
          setError("Invite link is incomplete.");
          setBooting(false);
          return;
        }

        const result = await previewStaffInvite({
          token,
          schoolId: schoolIdFromUrl,
          inviteId: inviteIdFromUrl,
        });

        if (!active) return;

        const payload = result?.data || {};
        const valid = !!payload.valid;
        const invite = payload.invite || null;
        const reason = String(payload.reason || "").trim();
        const resolvedInviteId = String(payload.inviteId || inviteIdFromUrl).trim();

        if (!valid || !invite) {
          const reasonMap = {
            "not-found": "Invite not found.",
            revoked: "This invite has been revoked.",
            rejected: "This invite is no longer valid.",
            deleted: "This invite is no longer available.",
            expired: "This invite has expired.",
            "missing-token": "This invite is missing its token.",
            "invalid-state": "This invite is not available for acceptance.",
            "already-used": "This invite has already been used.",
          };

          setError(reasonMap[reason] || "Invite unavailable.");
          setBooting(false);
          return;
        }

        const status = normalizeStatus(invite.status);
        if (["accepted", "active"].includes(status) && !invite.inviteToken) {
          setError("This invite has already been used.");
          setBooting(false);
          return;
        }

        const email = String(invite.email || "").trim().toLowerCase();
        const name =
          String(invite.displayName || "").trim() ||
          String(invite.name || "").trim() ||
          "";
        const sid = String(invite.schoolId || schoolIdFromUrl).trim();

        setInviteData(invite);
        setInviteEmail(email);
        setFullName(name);
        setSchoolId(sid);
        setInviteId(resolvedInviteId);

        if (email) {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (!active) return;
          setAccountMode(methods.length ? "existing" : "new");
        } else {
          setAccountMode("new");
        }

        setBooting(false);
      } catch (err) {
        console.error("Load invite failed:", err);
        if (!active) return;
        setError(friendlyError(err));
        setBooting(false);
      }
    }

    loadInvite();

    return () => {
      active = false;
    };
  }, [token, schoolIdFromUrl, inviteIdFromUrl, previewStaffInvite]);

  async function ensureCleanSessionForEmail(targetEmail) {
    const currentEmail = String(auth.currentUser?.email || "")
      .trim()
      .toLowerCase();

    if (auth.currentUser && currentEmail && currentEmail !== targetEmail) {
      await signOut(auth);
    }
  }

  async function handleSubmit() {
    try {
      setSubmitting(true);
      setError("");

      const cleanEmail = String(inviteEmail || "").trim().toLowerCase();
      const cleanName = String(fullName || "").trim();

      if (!token) {
        setError("Invite token is missing.");
        return;
      }
      if (!schoolId) {
        setError("Invite school is missing.");
        return;
      }
      if (!inviteId) {
        setError("Invite record is missing.");
        return;
      }
      if (!cleanEmail) {
        setError("Invite email is missing.");
        return;
      }
      if (!password || password.length < 6) {
        setError("Please enter a password with at least 6 characters.");
        return;
      }

      await ensureCleanSessionForEmail(cleanEmail);

      let userCredential;

      if (accountMode === "new") {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          cleanEmail,
          password
        );

        if (cleanName) {
          try {
            await updateProfile(userCredential.user, { displayName: cleanName });
          } catch (profileErr) {
            console.warn("Profile update skipped:", profileErr);
          }
        }
      } else {
        userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
      }

      const result = await acceptInviteCallable({
        token,
        uid: userCredential.user.uid,
        schoolId,
        inviteId,
      });

      const payload = result?.data || {};

      if (payload?.ok) {
        const redirectTo =
          payload.redirectTo ||
          (payload.role === "admin" ? "/admin-manager" : "/teacher-dashboard");

        navigate(redirectTo, { replace: true });
        return;
      }

      throw new Error("Invite acceptance did not complete.");
    } catch (err) {
      console.error("Accept invite failed:", err);
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (booting) {
    return (
      <div style={styles.page}>
        <div style={styles.centerCard}>
          <div style={styles.logoMini}>PRESENTPLUS</div>
          <h1 style={styles.centerTitle}>Preparing your invite</h1>
          <p style={styles.centerText}>
            We are loading your secure staff access details.
          </p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div style={styles.page}>
        <div style={styles.centerCard}>
          <div style={styles.logoMini}>PRESENTPLUS</div>
          <h1 style={styles.centerTitle}>Invite unavailable</h1>
          <p style={styles.centerText}>{error}</p>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const roleLabel = roleLabelFromInvite(inviteData);

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <section style={styles.leftPanel}>
          <div style={styles.leftEyebrow}>PresentPlus Staff Access</div>
          <h1 style={styles.leftTitle}>
            {accountMode === "new"
              ? "Create account to accept invite"
              : "Sign in to accept invite"}
          </h1>

          <p style={styles.leftText}>
            {accountMode === "new"
              ? "Finish setting up your account to activate staff access."
              : "This email already has an account. Sign in with the correct password to join the school."}
          </p>

          <div style={styles.infoGrid}>
            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>Invited role</div>
              <div style={styles.infoValue}>{roleLabel}</div>
            </div>

            <div style={styles.infoCard}>
              <div style={styles.infoLabel}>Account mode</div>
              <div style={styles.infoValue}>
                {accountMode === "new" ? "New account" : "Existing account"}
              </div>
            </div>
          </div>
        </section>

        <section style={styles.rightPanel}>
          <div style={styles.formEyebrow}>Secure acceptance</div>
          <h2 style={styles.formTitle}>Create your staff password</h2>
          <p style={styles.formText}>
            The invited email is locked to keep access secure.
          </p>

          {error ? <div style={styles.errorBox}>{error}</div> : null}

          <div style={styles.formGrid}>
            <div style={styles.inputWrap}>
              <label style={styles.label}>Full name</label>
<input
  style={{ ...styles.input, ...styles.lockedInput }}
  value={fullName}
  disabled
  readOnly
  placeholder="Full name"
/>
            </div>

            <div style={styles.inputWrap}>
              <label style={styles.label}>Invite email</label>
              <input
                style={{ ...styles.input, ...styles.lockedInput }}
                value={inviteEmail}
                disabled
                placeholder="Invite email"
              />
            </div>
          </div>

          <div style={styles.inputWrap}>
            <label style={styles.label}>
              {accountMode === "new" ? "Create password" : "Password"}
            </label>

            <div style={styles.passwordRow}>
              <input
                style={{ ...styles.input, marginBottom: 0 }}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={
                  accountMode === "new"
                    ? "Create your password"
                    : "Enter your password"
                }
                autoComplete={
                  accountMode === "new" ? "new-password" : "current-password"
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.showBtn}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              ...styles.primaryButton,
              ...(submitting ? styles.primaryButtonDisabled : {}),
            }}
          >
            {submitting
              ? "Processing..."
              : accountMode === "new"
              ? "Create Account & Accept"
              : "Accept Invite"}
          </button>

          {accountMode === "existing" ? (
            <button
              type="button"
              onClick={() => navigate("/reset-password")}
              style={styles.helperLinkButton}
            >
              Forgot password?
            </button>
          ) : null}
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#eef3fb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    boxSizing: "border-box",
  },
  shell: {
    width: "100%",
    maxWidth: 1180,
    minHeight: 640,
    borderRadius: 36,
    overflow: "hidden",
    background: "#ffffff",
    boxShadow: "0 30px 80px rgba(17, 32, 66, 0.10)",
    display: "grid",
    gridTemplateColumns: "1.06fr 0.94fr",
  },
  leftPanel: {
    background: "linear-gradient(135deg, #4f63ff 0%, #6f59ff 100%)",
    color: "#ffffff",
    padding: "52px 44px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  leftEyebrow: {
    fontSize: 14,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.92,
    marginBottom: 22,
  },
  leftTitle: {
    margin: 0,
    fontSize: 64,
    lineHeight: 0.95,
    letterSpacing: "-0.05em",
    fontWeight: 900,
    maxWidth: 470,
  },
  leftText: {
    marginTop: 28,
    fontSize: 17,
    lineHeight: 1.8,
    maxWidth: 500,
    color: "rgba(255,255,255,0.96)",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    marginTop: 34,
  },
  infoCard: {
    borderRadius: 24,
    padding: "20px",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(10px)",
  },
  infoLabel: {
    fontSize: 12,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.84,
    marginBottom: 10,
  },
  infoValue: {
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  rightPanel: {
    padding: "52px 40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    background: "#ffffff",
  },
  formEyebrow: {
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 800,
    marginBottom: 12,
  },
  formTitle: {
    margin: 0,
    fontSize: 36,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    fontWeight: 900,
    color: "#112042",
  },
  formText: {
    marginTop: 16,
    marginBottom: 28,
    color: "#667085",
    fontSize: 16,
    lineHeight: 1.7,
    maxWidth: 520,
  },
  errorBox: {
    marginBottom: 18,
    padding: 16,
    borderRadius: 18,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontWeight: 800,
    fontSize: 15,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    marginBottom: 20,
  },
  inputWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 18,
  },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: "#344054",
  },
  input: {
    width: "100%",
    padding: "18px 18px",
    borderRadius: 20,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 16,
    color: "#14213d",
    outline: "none",
    boxSizing: "border-box",
  },
  lockedInput: {
    background: "#f4f7fc",
    color: "#667085",
  },
  passwordRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
  },
  showBtn: {
    border: "1px solid #d8e1f2",
    background: "#ffffff",
    color: "#24324a",
    borderRadius: 18,
    padding: "16px 18px",
    fontWeight: 800,
    cursor: "pointer",
    minWidth: 88,
  },
  primaryButton: {
    width: "100%",
    border: "none",
    borderRadius: 20,
    padding: "20px 22px",
    background: "linear-gradient(135deg, #4f63ff 0%, #6f59ff 100%)",
    color: "#ffffff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
    boxShadow: "0 18px 34px rgba(79, 99, 255, 0.22)",
    marginTop: 8,
  },
  primaryButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  helperLinkButton: {
    marginTop: 18,
    alignSelf: "flex-start",
    border: "none",
    background: "transparent",
    color: "#4f63ff",
    textDecoration: "none",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    padding: 0,
  },
  centerCard: {
    width: "100%",
    maxWidth: 620,
    background: "#ffffff",
    borderRadius: 28,
    padding: "42px 34px",
    textAlign: "center",
    boxShadow: "0 26px 60px rgba(17, 32, 66, 0.08)",
  },
  logoMini: {
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 900,
    marginBottom: 18,
  },
  centerTitle: {
    margin: 0,
    color: "#112042",
    fontSize: 48,
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },
  centerText: {
    marginTop: 16,
    color: "#667085",
    fontSize: 16,
    lineHeight: 1.7,
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 26,
    padding: "16px 22px",
    borderRadius: 18,
    background: "#ffffff",
    border: "1px solid #d8e1f2",
    color: "#112042",
    fontWeight: 800,
    textDecoration: "none",
    cursor: "pointer",
  },
};